import React from 'react';
import { TicketDetail } from '../callCenterAllTicketsTypes';
import { PriorityBadge, StatusBadge } from '../../shared';
import { TicketStatusPipeline } from './TicketStatusPipeline';
import { ConversationThread } from './ConversationThread';
import { TicketSidebar } from './TicketSidebar';
import { TicketKnowledgeBasePanel } from './TicketKnowledgeBasePanel';
import styles from './TicketDetailPanel.module.css';

interface TicketDetailPanelProps {
  detail: TicketDetail;
  onEscalate?: () => void;
  onResolve?: () => void;
  /** Called when the user clicks "Send Reply" in the conversation thread. */
  onSendReply?: (body: string) => Promise<void> | void;
  /** Called from the sidebar's Update button with the dropdown values. */
  onUpdate?: (input: { status: string }) => Promise<void> | void;
  /** Called when the sidebar's Assigned-to select changes (null = unassign). */
  onAssign?: (userId: number | null) => Promise<void> | void;
  busy?: boolean;
}

export const TicketDetailPanel: React.FC<TicketDetailPanelProps> = ({
  detail, onEscalate, onResolve, onSendReply, onUpdate, onAssign, busy,
}) => {
  const isResolved = detail.status === 'Resolved' || detail.status === 'Closed';
  return (
    <div className={styles.panel}>
      <div className={styles.headerRow}>
        <div className={styles.badges}>
          <span className={styles.ticketIdBadge}>{detail.ticketId}</span>
          <PriorityBadge priority={detail.priority} />
          <StatusBadge status={detail.status} />
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.escalateBtn}
            onClick={() => onEscalate?.()}
            disabled={busy || isResolved}
          >
            {busy ? 'Working…' : 'Escalate'}
          </button>
          <button
            type="button"
            className={styles.resolveBtn}
            onClick={() => onResolve?.()}
            disabled={busy || isResolved}
          >
            {isResolved ? 'Resolved' : 'Resolve Ticket'}
          </button>
        </div>
      </div>

      <TicketStatusPipeline current={detail.pipelineStage} />

      <h3 className={styles.subject}>{detail.subject}</h3>
      <p className={styles.meta}>{detail.meta}</p>

      <div className={styles.body}>
        <div className={styles.mainColumn}>
          <ConversationThread messages={detail.thread} onSendReply={onSendReply} busy={busy} />
          <TicketKnowledgeBasePanel category={detail.module} subject={detail.subject} />
        </div>
        <TicketSidebar detail={detail} onUpdate={onUpdate} onAssign={onAssign} onEscalate={onEscalate} busy={busy} />
      </div>
    </div>
  );
};
